import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/http';

const createEmptyRow = () => ({
  name: '',
  origin: '',
  idNumber: '',
  jobType: '',
  certificateNumber: '',
  remark: ''
});

const formatChineseDate = (value) => {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length !== 3) return value;
  return `${parts[0]}年${parts[1]}月${parts[2]}日`;
};

const COMPANY_NAME = '紫金铜业有限公司';
const DOC_CODE = 'ZJTY/ES-BD-010';
const DEFAULT_ENTRUST_UNIT = '上杭县众邦劳务有限公司';

const TrainingEntrustPage = () => {
  const [entrustUnit, setEntrustUnit] = useState(DEFAULT_ENTRUST_UNIT);
  const [entrustDate, setEntrustDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(() => [createEmptyRow(), createEmptyRow(), createEmptyRow()]);
  const [entrustStatus, setEntrustStatus] = useState('通过');
  const [letterContent, setLetterContent] = useState('');
  const [showLetterEditor, setShowLetterEditor] = useState(false);
  const [records, setRecords] = useState([]);
  const [activeRecordId, setActiveRecordId] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const formattedDate = useMemo(() => formatChineseDate(entrustDate), [entrustDate]);
  const filledCount = useMemo(
    () => rows.filter((row) => row.name && row.name.trim() !== '').length,
    [rows]
  );

  const displayEntrustUnit = useMemo(() => entrustUnit.trim(), [entrustUnit]);

  const fetchRecords = async () => {
    setLoadingRecords(true);
    try {
      const res = await api.get('/education-training/entrust-batches');
      const list = res?.data?.records;
      setRecords(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load training entrust records', error);
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleRowChange = (index, field, value) => {
    setRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = () => {
    setRows((prev) => (prev.length > 1 ? prev.slice(0, prev.length - 1) : prev));
  };

  const handleReset = () => {
    setEntrustUnit(DEFAULT_ENTRUST_UNIT);
    setEntrustDate(new Date().toISOString().slice(0, 10));
    setRows([createEmptyRow(), createEmptyRow(), createEmptyRow()]);
    setEntrustStatus('通过');
    setLetterContent('');
    setShowLetterEditor(false);
    setActiveRecordId(null);
  };

  const openCreateModal = () => {
    handleReset();
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setLoadingDetail(false);
    setSaving(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const cloneRows = (list) => list.map((row) => ({ ...row }));

  const handleSaveRecord = async () => {
    const normalizedUnit = entrustUnit && entrustUnit.trim() ? entrustUnit.trim() : DEFAULT_ENTRUST_UNIT;
    const payload = {
      entrustUnit: normalizedUnit,
      entrustDate,
      status: entrustStatus,
      letterContent,
      rows: cloneRows(rows)
    };

    setSaving(true);
    try {
      if (activeRecordId) {
        await api.put(`/education-training/entrust-batches/${activeRecordId}`, payload);
      } else {
        const res = await api.post('/education-training/entrust-batches', payload);
        const newId = res?.data?.id;
        if (newId) setActiveRecordId(newId);
      }
      setEntrustUnit(normalizedUnit);
      await fetchRecords();
      closeModal();
    } catch (error) {
      console.error('Failed to save training entrust record', error);
      alert(error?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleEditRecord = async (record) => {
    const recordId = record?.id;
    if (!recordId) return;
    try {
      setLoadingDetail(true);
      setModalVisible(true);
      const res = await api.get(`/education-training/entrust-batches/${recordId}`);
      const detail = res?.data?.record;
      setEntrustUnit(detail?.entrustUnit || DEFAULT_ENTRUST_UNIT);
      setEntrustDate(detail?.entrustDate || new Date().toISOString().slice(0, 10));
      setEntrustStatus(detail?.status || '通过');
      setLetterContent(detail?.letterContent || '');
      setRows(detail?.rows && detail.rows.length ? cloneRows(detail.rows) : [createEmptyRow(), createEmptyRow(), createEmptyRow()]);
      setActiveRecordId(detail?.id || recordId);
      setShowLetterEditor(false);
    } catch (error) {
      console.error('Failed to load training entrust record detail', error);
      alert(error?.message || '加载详情失败');
      closeModal();
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!id) return;
    const ok = window.confirm('确认删除该委托批次吗？');
    if (!ok) return;
    try {
      await api.delete(`/education-training/entrust-batches/${id}`);
      if (activeRecordId === id) {
        handleReset();
      }
      await fetchRecords();
    } catch (error) {
      console.error('Failed to delete training entrust record', error);
      alert(error?.message || '删除失败');
    }
  };

  const styles = `
    .training-entrust-container { max-width: 1120px; margin: 0 auto; display: flex; flex-direction: column; gap: 24px; }
    .summary-panel { display: flex; flex-direction: column; gap: 16px; padding: 18px 22px; background: #fff; border-radius: 12px; box-shadow: 0 8px 28px rgba(15,23,42,0.08); }
    .summary-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
    .summary-title { display: flex; flex-direction: column; gap: 6px; }
    .summary-title h2 { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; }
    .summary-subtitle { font-size: 13px; color: #64748b; }
    .summary-actions { display: flex; gap: 10px; flex-wrap: wrap; }
    .primary-btn { padding: 10px 18px; border-radius: 10px; border: none; background: #2563eb; color: #fff; cursor: pointer; font-size: 14px; font-weight: 700; box-shadow: 0 8px 18px rgba(37,99,235,0.24); transition: transform 0.15s ease, box-shadow 0.2s ease; }
    .primary-btn:hover { transform: translateY(-1px); box-shadow: 0 12px 20px rgba(37,99,235,0.28); }
    .secondary-btn { padding: 10px 18px; border-radius: 10px; border: 1px solid var(--border-color); background: #f4f6fb; color: #1f2937; cursor: pointer; font-size: 14px; font-weight: 700; box-shadow: none; }
    .secondary-btn:hover { background: #e2e8f0; }
    .stats-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
    @media (max-width: 900px) { .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 640px) { .stats-grid { grid-template-columns: 1fr; } }
    .stat-card { background: #f8fafc; border-radius: 12px; padding: 14px 16px; border: 1px solid #e2e8f0; display: flex; flex-direction: column; gap: 8px; }
    .stat-label { font-size: 12px; color: #64748b; letter-spacing: 1px; }
    .stat-value { font-size: 22px; font-weight: 800; color: #0f172a; }
    .stat-breakdown { display: flex; gap: 12px; flex-wrap: wrap; align-items: baseline; }
    .stat-breakdown span { font-size: 14px; color: #0f172a; font-weight: 800; }
    .stat-breakdown span em { font-style: normal; font-size: 12px; font-weight: 700; color: #64748b; margin-right: 6px; }
    .printable-area { display: none; flex-direction: column; gap: 28px; }
    .entrust-letter, .entrust-sheet { background: #fff; padding: 40px 56px; border-radius: 14px; box-shadow: 0 10px 32px rgba(15,23,42,0.08); position: relative; }
    .entrust-letter { min-height: 680px; display: flex; flex-direction: column; gap: 20px; page: portrait-page; }
    .entrust-letter.screen-hidden { display: none; }
    .letter-title { text-align: center; font-size: 26px; font-weight: 600; letter-spacing: 4px; margin-bottom: 16px; }
    .letter-body { font-size: 15px; color: #111827; line-height: 1.85; display: flex; flex-direction: column; gap: 14px; }
    .letter-body p { margin: 0; }
    .letter-body p.indent { text-indent: 2em; }
    .letter-item { position: relative; padding-left: 2.4em; line-height: 1.75; }
    .letter-item span { position: absolute; left: 0; }
    .letter-signature { margin-top: auto; align-self: flex-end; display: flex; flex-direction: column; gap: 12px; font-size: 14px; color: #0f172a; width: 320px; text-align: left; }
    .signature-line { display: flex; align-items: baseline; gap: 6px; }
    .signature-label { flex: 0 0 auto; font-weight: 500; }
    .signature-label::after { content: '：'; margin-left: 4px; }
    .signature-blank { flex: 1; min-width: calc(11ch + 12px); border-bottom: 1px solid #0f172a; padding: 0 4px; display: flex; align-items: center; }
    .signature-input { width: 100%; border: none; background: transparent; font-size: 14px; padding: 2px 0; }
    .signature-input:focus { outline: none; }
    .signature-print-value { display: none; font-size: 14px; color: #0f172a; }
    .entrust-sheet { min-height: 680px; page: landscape-page; }
    .sheet-title { text-align: center; font-size: 24px; font-weight: 600; letter-spacing: 4px; margin-bottom: 22px; }
    .sheet-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 16px; gap: 24px; }
    .sheet-code { font-size: 14px; font-weight: 600; color: #111827; }
    .meta-lines { display: flex; justify-content: space-between; gap: 32px; margin-bottom: 18px; }
    .meta-line { display: flex; align-items: center; gap: 10px; font-size: 15px; color: #0f172a; }
    .meta-line .meta-label { font-weight: 600; letter-spacing: 2px; }
    .meta-line .meta-value { display: inline-block; min-width: 260px; border-bottom: 1px solid #0f172a; padding: 6px 10px; font-size: 15px; }
    .entrust-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 14px; color: #0f172a; }
    .entrust-table th, .entrust-table td { border: 1px solid #0f172a; padding: 8px 10px; }
    .entrust-table th { text-align: center; font-weight: 600; background: #f8fafc; }
    .entrust-table col.seq { width: 7%; }
    .entrust-table col.name { width: 14%; }
    .entrust-table col.origin { width: 16%; }
    .entrust-table col.id-card { width: 26%; }
    .entrust-table col.job { width: 15%; }
    .entrust-table col.certificate-number { width: 14%; }
    .entrust-table col.remark { width: 12%; }
    .cell-wrap { position: relative; min-height: 28px; display: flex; align-items: center; }
    .cell-wrap input, .cell-wrap textarea { width: 100%; border: none; background: transparent; font-size: 14px; padding: 2px; }
    .cell-wrap textarea { min-height: 52px; resize: vertical; }
    .cell-wrap input:focus, .cell-wrap textarea:focus { outline: 1px solid #2563eb; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); border-radius: 4px; }
    .print-only { display: none; white-space: pre-wrap; word-break: break-all; }
    .doc-code { margin-top: 24px; font-size: 12px; color: #6b7280; }
    .records-panel { background: #fff; border-radius: 12px; box-shadow: 0 10px 30px rgba(15,23,42,0.06); padding: 20px 24px; }
    .records-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 18px; }
    .records-header h3 { margin: 0; font-size: 18px; font-weight: 600; color: #0f172a; }
    .records-title { display: flex; flex-direction: column; gap: 6px; }
    .records-subtitle { font-size: 13px; color: #64748b; }
    .records-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 14px; }
    .bill-card { position: relative; background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 10px 28px rgba(15,23,42,0.06); }
    .bill-body { position: relative; z-index: 1; padding: 14px 16px 12px; display: flex; flex-direction: column; gap: 10px; }
    .bill-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
    .bill-title { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
    .bill-title strong { font-size: 15px; color: #0f172a; font-weight: 800; line-height: 1.25; }
    .bill-sub { font-size: 12px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .bill-right { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 0 0 auto; }
    .bill-stamp { width: 62px; height: 62px; border-radius: 999px; border: 2px solid rgba(220, 38, 38, 0.35); color: rgba(220, 38, 38, 0.7); font-weight: 900; font-size: 12px; display: flex; align-items: center; justify-content: center; transform: rotate(-12deg); user-select: none; flex: 0 0 auto; }
    .bill-status { font-size: 12px; line-height: 18px; padding: 2px 10px; border-radius: 999px; border: 1px solid rgba(0,0,0,0.12); background: rgba(0,0,0,0.02); color: rgba(0,0,0,0.72); user-select: none; }
    .bill-status.pass { background: rgba(82,196,26,0.10); border-color: rgba(82,196,26,0.25); color: rgba(56,158,13,1); }
    .bill-status.train { background: rgba(24,144,255,0.10); border-color: rgba(24,144,255,0.25); color: rgba(24,144,255,1); }
    .bill-status.void { background: rgba(245,34,45,0.10); border-color: rgba(245,34,45,0.25); color: rgba(245,34,45,1); }
    .bill-names { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 12px; font-size: 13px; color: #0f172a; line-height: 1.6; min-height: 52px; }
    .bill-footer { position: relative; z-index: 1; padding: 12px 112px 14px 16px; border-top: 1px dashed #cbd5e1; display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; background: #fff; }
    .bill-footer button { padding: 7px 12px; border-radius: 10px; border: 1px solid var(--border-color); background: #f1f5f9; cursor: pointer; font-size: 12px; color: #0f172a; font-weight: 700; }
    .bill-footer button:hover { background: #e2e8f0; }
    .bill-footer button.delete { border-color: #f87171; color: #b91c1c; background: #fee2e2; }
    .bill-footer button.delete:hover { background: #fecaca; }
    .bill-tear { position: absolute; top: 0; bottom: 0; right: 92px; width: 1px; background: repeating-linear-gradient(to bottom, #cbd5e1 0, #cbd5e1 6px, transparent 6px, transparent 12px); opacity: 0.9; z-index: 0; pointer-events: none; }
    .bill-notch { position: absolute; right: 92px; width: 22px; height: 22px; background: #fff; border: 1px solid #e2e8f0; border-left-color: transparent; border-bottom-color: transparent; border-radius: 999px; transform: translateX(50%) rotate(45deg); z-index: 0; pointer-events: none; }
    .bill-notch.top { top: 18px; }
    .bill-notch.bottom { bottom: 18px; }

    .status-toggle { display: inline-flex; gap: 8px; flex-wrap: wrap; }
    .status-toggle button { border-radius: 999px; padding: 8px 12px; border: 1px solid var(--border-color); background: #f8fafc; cursor: pointer; font-size: 13px; font-weight: 700; color: #0f172a; }
    .status-toggle button:hover { background: #eef2ff; }
    .status-toggle button.active { border-color: rgba(37,99,235,0.55); background: rgba(37,99,235,0.10); color: #1d4ed8; }

    .modal-mask { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55); display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; z-index: 9999; }
    .modal-card { width: min(1120px, 100%); background: #fff; border-radius: 14px; box-shadow: 0 18px 46px rgba(15,23,42,0.25); overflow: hidden; }
    .modal-header { padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .modal-header h3 { margin: 0; font-size: 16px; font-weight: 800; color: #0f172a; }
    .modal-body { padding: 18px 20px 22px; display: flex; flex-direction: column; gap: 16px; }
    .modal-fields { display: grid; grid-template-columns: 1fr 220px; gap: 14px; }
    .field { display: flex; flex-direction: column; gap: 6px; }
    .field label { font-size: 13px; color: #475569; font-weight: 600; }
    .field input { padding: 10px 12px; border-radius: 10px; border: 1px solid var(--border-color); font-size: 14px; background: #f8fafc; transition: border 0.2s, box-shadow 0.2s; }
    .field input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); outline: none; }
    .field textarea { padding: 10px 12px; border-radius: 10px; border: 1px solid var(--border-color); font-size: 14px; background: #f8fafc; transition: border 0.2s, box-shadow 0.2s; resize: vertical; min-height: 120px; }
    .field textarea:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); outline: none; }
    .letter-text { white-space: pre-wrap; word-break: break-word; }
    .modal-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; }
    .modal-toolbar-left { display: flex; gap: 10px; flex-wrap: wrap; }
    .modal-toolbar-right { display: flex; gap: 10px; flex-wrap: wrap; }
    .hint { font-size: 12px; color: #64748b; }
    @page { size: A4 landscape; margin: 0; }
    @page:first { size: A4 portrait; margin: 0; }
    @page portrait-page { size: A4 portrait; margin: 0; }
    @page landscape-page { size: A4 landscape; margin: 0; }
    @media print {
      body { background: #fff; margin: 0; }
      .no-print { display: none !important; }
      body * { visibility: hidden; }
      .training-entrust-container { max-width: none !important; margin: 0 !important; }
      .printable-area, .printable-area * { visibility: visible; }
      .printable-area { display: flex; position: absolute; top: 0; left: 0; right: 0; margin: 0 auto; width: 100%; }
      .entrust-letter, .entrust-sheet { box-shadow: none; border-radius: 0; padding: 14mm 14mm; width: 100%; }
      .entrust-letter.screen-hidden { display: flex; }
      .entrust-letter { page-break-after: always; }
      .meta-line .meta-value { border-bottom: 1px solid #000; }
      .signature-input { display: none; }
      .signature-print-value { display: inline; }
      .cell-wrap input, .cell-wrap textarea { display: none; }
      .print-only { display: block; font-size: 13px; }
    }
  `;

  const totalParticipants = useMemo(() => {
    return records.reduce((sum, item) => sum + (Number(item.participantCount) || 0), 0);
  }, [records]);

  const statusCounts = useMemo(() => {
    return records.reduce(
      (acc, item) => {
        const status = item?.status || '通过';
        if (status === '作废') acc.void += 1;
        else if (status === '培训') acc.train += 1;
        else acc.pass += 1;
        return acc;
      },
      { pass: 0, train: 0, void: 0 }
    );
  }, [records]);

  return (
    <div className="training-entrust-container">
      <style>{styles}</style>
      <div className="summary-panel no-print">
        <div className="summary-header">
          <div className="summary-title">
            <h2>培训委托</h2>
            <div className="summary-subtitle">默认委托单位：{DEFAULT_ENTRUST_UNIT}</div>
          </div>
          <div className="summary-actions">
            <button type="button" className="primary-btn" onClick={openCreateModal}>新增委托单</button>
          </div>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">委托单数量</div>
            <div className="stat-value">{records.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">参与人员总数</div>
            <div className="stat-value">{totalParticipants}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">状态分布</div>
            <div className="stat-breakdown">
              <span><em>通过</em>{statusCounts.pass}</span>
              <span><em>培训</em>{statusCounts.train}</span>
              <span><em>作废</em>{statusCounts.void}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="records-panel no-print">
        <div className="records-header">
          <div className="records-title">
            <h3>委托单列表</h3>
            <span className="records-subtitle">点击“编辑”在弹窗中维护委托单与人员信息</span>
          </div>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            共 {records.length} 条
          </span>
        </div>
        {loadingRecords ? (
          <div style={{ fontSize: 13, color: '#6b7280' }}>加载中...</div>
        ) : records.length === 0 ? (
          <div style={{ fontSize: 13, color: '#6b7280' }}>暂无保存记录，请填写表单后点击保存。</div>
        ) : (
          <div className="records-list">
            {records.map((record) => {
              const displayDate = record.entrustDate || '';
              const displayUnit = record.entrustUnit || '';
              const names = record.participantNames && record.participantNames.trim()
                ? record.participantNames
                : '未填写姓名';
              const status = record.status || '通过';
              const statusClass = status === '作废' ? 'void' : status === '培训' ? 'train' : 'pass';
              return (
                <div className="bill-card" key={record.id}>
                  <div className="bill-tear" />
                  <div className="bill-notch top" />
                  <div className="bill-notch bottom" />
                  <div className="bill-body">
                    <div className="bill-header">
                      <div className="bill-title">
                        <strong>{displayDate || '未填写日期'}</strong>
                        <div className="bill-sub">{displayUnit || '未填写委托单位'}</div>
                      </div>
                      <div className="bill-right">
                        <div className={`bill-status ${statusClass}`}>{status}</div>
                        <div className="bill-stamp">委托单</div>
                      </div>
                    </div>
                    <div className="bill-names">{names}</div>
                  </div>
                  <div className="bill-footer">
                    <button type="button" onClick={() => handleEditRecord(record)}>编辑</button>
                    <button type="button" className="delete" onClick={() => handleDeleteRecord(record.id)}>删除</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalVisible ? (
        <div className="modal-mask no-print" onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}>
          <div className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>{activeRecordId ? '编辑委托单' : '新增委托单'}</h3>
              <div className="modal-toolbar-right">
                <button type="button" className="secondary-btn" onClick={closeModal}>关闭</button>
              </div>
            </div>
            <div className="modal-body">
              {loadingDetail ? (
                <div style={{ fontSize: 13, color: '#6b7280' }}>加载中...</div>
              ) : (
                <>
                  <div className="modal-fields">
                    <div className="field">
                      <label>委托单位（可修改）</label>
                      <input
                        type="text"
                        value={entrustUnit}
                        onChange={(event) => setEntrustUnit(event.target.value)}
                        placeholder="请输入委托单位"
                      />
                    </div>
                    <div className="field">
                      <label>委托日期</label>
                      <input
                        type="date"
                        value={entrustDate}
                        onChange={(event) => setEntrustDate(event.target.value)}
                      />
                    </div>
                  </div>

                  <div className="field" style={{ maxWidth: 520 }}>
                    <label>状态</label>
                    <div className="status-toggle">
                      {['通过', '培训', '作废'].map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={entrustStatus === s ? 'active' : ''}
                          onClick={() => setEntrustStatus(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="modal-toolbar">
                    <div className="modal-toolbar-left">
                      <button type="button" className="secondary-btn" onClick={handleAddRow}>增加一行</button>
                      <button type="button" className="secondary-btn" onClick={handleRemoveRow}>删除末行</button>
                      <button
                        type="button"
                        className="secondary-btn"
                        onClick={() => setShowLetterEditor((v) => !v)}
                      >
                        {showLetterEditor ? '收起委托书' : '编辑委托书'}
                      </button>
                      <span className="hint">已填写姓名：{filledCount} 人</span>
                    </div>
                    <div className="modal-toolbar-right">
                      <button type="button" className="secondary-btn" onClick={handlePrint}>打印</button>
                      <button type="button" className="primary-btn" onClick={handleSaveRecord} disabled={saving}>
                        {saving ? '保存中...' : (activeRecordId ? '保存修改' : '保存记录')}
                      </button>
                    </div>
                  </div>

                  {showLetterEditor ? (
                    <div className="field" style={{ marginTop: 6 }}>
                      <label>公司级安全教育培训委托书正文（可编辑，留空则使用默认模板）</label>
                      <textarea
                        value={letterContent}
                        onChange={(e) => setLetterContent(e.target.value)}
                        placeholder="请输入委托书正文内容（支持换行）"
                      />
                    </div>
                  ) : null}

                  <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                    <table className="entrust-table" style={{ margin: 0 }}>
                      <colgroup>
                        <col className="seq" />
                        <col className="name" />
                        <col className="origin" />
                        <col className="id-card" />
                        <col className="job" />
                        <col className="certificate-number" />
                        <col className="remark" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>序号</th>
                          <th>姓名</th>
                          <th>籍贯</th>
                          <th>身份证号</th>
                          <th>工种</th>
                          <th>特种作业人员持证证号</th>
                          <th>备注</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={`modal-row-${index}`}>
                            <td style={{ textAlign: 'center' }}>{index + 1}</td>
                            <td>
                              <div className="cell-wrap">
                                <input
                                  type="text"
                                  value={row.name}
                                  onChange={(event) => handleRowChange(index, 'name', event.target.value)}
                                  placeholder="姓名"
                                  maxLength={20}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="cell-wrap">
                                <input
                                  type="text"
                                  value={row.origin}
                                  onChange={(event) => handleRowChange(index, 'origin', event.target.value)}
                                  placeholder="籍贯"
                                  maxLength={30}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="cell-wrap">
                                <input
                                  type="text"
                                  value={row.idNumber}
                                  onChange={(event) => handleRowChange(index, 'idNumber', event.target.value)}
                                  placeholder="身份证号"
                                  maxLength={18}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="cell-wrap">
                                <input
                                  type="text"
                                  value={row.jobType}
                                  onChange={(event) => handleRowChange(index, 'jobType', event.target.value)}
                                  placeholder="工种"
                                  maxLength={30}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="cell-wrap">
                                <input
                                  type="text"
                                  value={row.certificateNumber}
                                  onChange={(event) => handleRowChange(index, 'certificateNumber', event.target.value)}
                                  placeholder="证号"
                                  maxLength={32}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="cell-wrap">
                                <textarea
                                  value={row.remark}
                                  onChange={(event) => handleRowChange(index, 'remark', event.target.value)}
                                  placeholder="备注"
                                  rows={2}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="printable-area">
        <div className="entrust-letter screen-hidden">
          <div className="letter-title">公司级安全教育培训委托书</div>
          {letterContent && letterContent.trim() ? (
            <div className="letter-body">
              <div className="letter-text">{letterContent}</div>
            </div>
          ) : (
            <div className="letter-body">
              <p>业务主管部门：</p>
              <p className="indent">
                根据{COMPANY_NAME}《安全生产培训办法》要求，此次我项目部新进作业人员（{filledCount}人），经我项目部确认符合以下要求：
              </p>
              <div className="letter-item"><span>（一）</span>新员工身体健康状态良好，无患有高血压（Ⅱ期以上）、心脏病、癫痫病等或存在吸毒、嗜酒等不良嗜好的员工从事相关职业禁忌的作业，并在7天之内提交前职业健康体检报告。</div>
              <div className="letter-item"><span>（二）</span>新员工应具备初中及以上文化。</div>
              <div className="letter-item"><span>（三）</span>新员工年龄不大于55周岁。</div>
              <div className="letter-item"><span>（四）</span>涉及特种作业人员证件齐全且符合作业要求。</div>
              <div className="letter-item"><span>（五）</span>劳动或劳务合同已经双方签署生效。</div>
              <div className="letter-item"><span>（六）</span>7天之内为聘用员工购买安全生产责任保险。</div>
              <p className="indent">附件：{COMPANY_NAME}安全教育培训人员信息表</p>
            </div>
          )}
          <div className="letter-signature">
            <div className="signature-line">
              <span className="signature-label">委托单位名称</span>
              <span className="signature-blank">
                <span className="signature-print-value">{entrustUnit || ' '}</span>
              </span>
            </div>
            <div className="signature-line">
              <span className="signature-label">项目部确认</span>
              <span className="signature-blank">
                <span className="signature-print-value">{' '}</span>
              </span>
            </div>
            <div className="signature-line">
              <span className="signature-label">业务主管部门审核</span>
              <span className="signature-blank">
                <span className="signature-print-value">{' '}</span>
              </span>
            </div>
            <div className="signature-line">
              <span className="signature-label">应急与安全生产部门审核</span>
              <span className="signature-blank">
                <span className="signature-print-value">{' '}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="entrust-sheet">
          <div className="sheet-title">公司级安全教育培训人员信息表</div>
          <div className="sheet-header">
            <span className="sheet-code">编号：{DOC_CODE}</span>
          </div>
          <div className="meta-lines">
            <div className="meta-line">
              <span className="meta-label">委托单位：</span>
              <span className="meta-value">{displayEntrustUnit || ' '}</span>
            </div>
            <div className="meta-line" style={{ justifyContent: 'flex-end' }}>
              <span className="meta-label">委托日期：</span>
              <span className="meta-value" style={{ minWidth: 160 }}>{formattedDate || ' '}</span>
            </div>
          </div>

          <table className="entrust-table">
            <colgroup>
              <col className="seq" />
              <col className="name" />
              <col className="origin" />
              <col className="id-card" />
              <col className="job" />
              <col className="certificate-number" />
              <col className="remark" />
            </colgroup>
            <thead>
              <tr>
                <th>序号</th>
                <th>姓名</th>
                <th>籍贯</th>
                <th>身份证号</th>
                <th>工种</th>
                <th>特种作业人员持证证号</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`row-${index}`}>
                  <td style={{ textAlign: 'center' }}>{index + 1}</td>
                  <td>
                    <div className="cell-wrap">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(event) => handleRowChange(index, 'name', event.target.value)}
                        placeholder="姓名"
                        maxLength={20}
                      />
                      <span className="print-only">{row.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cell-wrap">
                      <input
                        type="text"
                        value={row.origin}
                        onChange={(event) => handleRowChange(index, 'origin', event.target.value)}
                        placeholder="籍贯"
                        maxLength={30}
                      />
                      <span className="print-only">{row.origin}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cell-wrap">
                      <input
                        type="text"
                        value={row.idNumber}
                        onChange={(event) => handleRowChange(index, 'idNumber', event.target.value)}
                        placeholder="身份证号"
                        maxLength={18}
                      />
                      <span className="print-only">{row.idNumber}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cell-wrap">
                      <input
                        type="text"
                        value={row.jobType}
                        onChange={(event) => handleRowChange(index, 'jobType', event.target.value)}
                        placeholder="工种"
                        maxLength={30}
                      />
                      <span className="print-only">{row.jobType}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cell-wrap">
                      <input
                        type="text"
                        value={row.certificateNumber}
                        onChange={(event) => handleRowChange(index, 'certificateNumber', event.target.value)}
                        placeholder="证号"
                        maxLength={32}
                      />
                      <span className="print-only">{row.certificateNumber}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cell-wrap">
                      <textarea
                        value={row.remark}
                        onChange={(event) => handleRowChange(index, 'remark', event.target.value)}
                        placeholder="备注"
                        rows={2}
                      />
                      <span className="print-only">{row.remark}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrainingEntrustPage;
